// Warmies Mission Control — daily operating view.
// SPEND · SHOPIFY revenue · MER. Attribution has its own tab. Revenue by state is
// a US choropleth; customer base is a new-vs-returning donut. Backed by
// GET /api/warmies/mission-control (tenant-cached).

import { useEffect, useMemo, useState } from 'react'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement,
  BarElement, BarController, LineController, ArcElement, Filler, Tooltip, Legend,
} from 'chart.js'
import { Line, Doughnut } from 'react-chartjs-2'
import { authFetch } from '../lib/api'
import { StateRevenueMap } from '../components/StateRevenueMap'
import './CAStyles.css'
import './IndexCA.css'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, BarElement,
                 BarController, LineController, ArcElement, Filler, Tooltip, Legend)

interface WeekRev { week: string; revenue: number; orders: number }
interface Channel {
  channel: string; spend_total: number; spend_7d: number | null; spend_30d: number | null
  has_trend: boolean; weekly: { week: string; spend: number }[]; source: string
}
interface Payload {
  status: string; tenant: string; data_through: string
  headline: { revenue_7d: number; revenue_30d: number; revenue_all: number
              orders_30d: number; aov: number; mer_30d: number | null }
  revenue_weekly: WeekRev[]
  customers: { total_customers: number; one_time_customers: number; repeat_customers: number
               repeat_purchase_rate: number; avg_lifetime_value: number; avg_purchase_frequency: number }
  channels: Channel[]
  meta_weekly: { week: string; spend: number }[]
  google_weekly?: { week: string; spend: number }[]
  geo_states: { state: string; revenue: number; orders: number }[]
}

const fmt$ = (n: number | null | undefined): string => {
  if (n == null) return '—'
  const a = Math.abs(n)
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (a >= 1_000)     return `$${(n / 1_000).toFixed(1)}k`
  return `$${Math.round(n)}`
}
const fmtN = (n: number | null | undefined): string => n == null ? '—' : n.toLocaleString('en-US')
const fmtPct = (r: number | null | undefined): string => {
  if (r == null) return '—'
  const v = r <= 1 ? r * 100 : r
  return `${v.toFixed(1)}%`
}
const niceWeek = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export function IndexWarmies() {
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const r = await authFetch('/api/warmies/mission-control')
        const j = await r.json()
        if (!alive) return
        if (j.status !== 'success') { setError(j.error || j.status || 'No data'); setData(null) }
        else setData(j)
      } catch (e: any) {
        if (alive) setError(e?.message || 'Request failed')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  const trendChart = useMemo(() => {
    const wk = data?.revenue_weekly || []
    if (!wk.length) return null
    // Sum every daily-feed channel rather than naming Meta and Google explicitly.
    // TikTok was added 2026-08-14 and a hardcoded pair would have silently left it
    // out of the spend line while the KPI tiles counted it.
    const dailyChannels = (data?.channels || []).filter((c) => c.has_trend)
    const byWeek = dailyChannels.map((c) => new Map((c.weekly || []).map((p) => [p.week, p.spend])))
    const paidFor = (w: string): number | null => {
      const vals = byWeek.map((m) => m.get(w)).filter((v) => v != null) as number[]
      if (!vals.length) return null
      return vals.reduce((a, b) => a + b, 0)
    }
    const paidLabel = `Ad spend (${dailyChannels.map((c) => c.channel).join(' + ') || 'paid'})`
    return {
      labels: wk.map((p) => niceWeek(p.week)),
      datasets: [
        {
          label: 'Revenue', data: wk.map((p) => p.revenue),
          borderColor: '#00C26E', backgroundColor: 'rgba(0,194,110,0.12)',
          borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, tension: 0.35, fill: true,
        },
        {
          label: paidLabel, data: wk.map((p) => paidFor(p.week)),
          borderColor: 'rgba(148,163,184,0.95)', borderWidth: 1.5, borderDash: [4, 3],
          pointRadius: 0, tension: 0.3, fill: false,
        },
      ],
    }
  }, [data])

  const chartOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: true, labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c: any) => `  ${c.dataset.label}: ${fmt$(c.parsed.y)}` } } },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } },
      y: { ticks: { callback: (v: any) => fmt$(v) } },
    },
  }), [])

  const custDonut = useMemo(() => {
    const cu = data?.customers
    if (!cu || (!cu.one_time_customers && !cu.repeat_customers)) return null
    return {
      labels: ['New (one-time)', 'Returning'],
      datasets: [{
        data: [cu.one_time_customers, cu.repeat_customers],
        backgroundColor: ['#CBD5E1', '#00C26E'],
        borderColor: '#FFFFFF', borderWidth: 2, hoverOffset: 4,
      }],
    }
  }, [data])

  const donutOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false, cutout: '64%',
    plugins: { legend: { position: 'bottom' as const, labels: { boxWidth: 10, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c: any) => `  ${c.label}: ${c.parsed.toLocaleString()}` } } },
  }), [])

  if (loading) return <div className="cax-load">Loading mission control…</div>
  if (error)   return <div className="cax-load err">Error: {error}</div>
  if (!data)   return <div className="cax-load">No data</div>

  const h = data.headline
  const c = data.customers
  // Headline ad spend must match MER's denominator. It previously showed Meta only
  // while the API divided revenue by every daily-feed channel, so the two tiles
  // disagreed — and adding TikTok (2026-08-14) widened the gap.
  const dailyCh = data.channels.filter((ch) => ch.has_trend)
  const paidSpend30d = dailyCh.reduce((s, ch) => s + (ch.spend_30d ?? 0), 0)
  const paidNames = dailyCh.map((ch) => ch.channel).join(' + ') || 'paid'

  const kpis: { label: string; value: string; sub?: string; accent?: boolean }[] = [
    { label: 'Revenue · 30d', value: fmt$(h.revenue_30d), sub: `${fmt$(h.revenue_7d)} last 7d`, accent: true },
    { label: 'Ad spend · 30d', value: fmt$(paidSpend30d), sub: paidNames },
    { label: 'MER · 30d', value: h.mer_30d != null ? `${h.mer_30d.toFixed(2)}x` : '—', sub: `revenue ÷ ${paidNames} spend`, accent: true },
    { label: 'Orders · 30d', value: fmtN(h.orders_30d) },
    { label: 'AOV', value: fmt$(h.aov) },
  ]

  return (
    <div className="cax-wrap">
      <div className="cax-head">
        <div>
          <div className="cax-eyebrow">Mission Control · Warmies</div>
          <h1 className="cax-h1">Daily Operating View</h1>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>through {data.data_through}</div>
      </div>

      {/* Headline KPIs */}
      <div className="cax-kpi-row">
        {kpis.map((k) => (
          <div className="cax-kpi" key={k.label} style={k.accent ? { borderColor: 'var(--accent)' } : undefined}>
            <div className="cax-kpi-label">{k.label}</div>
            <div className="cax-kpi-value">{k.value}</div>
            {k.sub && <div className="cax-kpi-delta" style={{ color: 'var(--text-muted)' }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Revenue vs Ad spend trend */}
      <div style={panel}>
        <div style={panelHead}>
          <div style={{ fontWeight: 600 }}>Revenue vs ad spend, weekly (16 wks)</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Shopify revenue · paid spend (daily-feed channels)</div>
        </div>
        <div style={{ height: 300 }}>
          {trendChart ? <Line data={trendChart} options={chartOpts as any} /> : <div className="cax-load">No history</div>}
        </div>
      </div>

      {/* Spend by channel + Customer base donut */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <div style={panel}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Ad spend by channel</div>
          {/* auto-fit rather than a fixed 3 columns — the channel list grows (TikTok
              added 2026-08-14) and a hardcoded count silently drops the newest one */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
            {data.channels.map((ch) => (
              <Mini key={ch.channel} label={ch.channel}
                    value={ch.has_trend ? fmt$(ch.spend_30d) : fmt$(ch.spend_total)}
                    sub={ch.has_trend ? '30d' : (ch.spend_total ? 'lifetime' : 'no data')} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
            {data.channels.filter((c) => c.has_trend).map((c) => c.channel).join(', ')} are daily;{' '}
            {data.channels.filter((c) => !c.has_trend).map((c) => c.channel).join(', ') || 'none'} lifetime only
            (no daily feed yet). MER = revenue ÷ daily-feed spend.
          </div>
        </div>
        <div style={panel}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Customer base</div>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 14, alignItems: 'center' }}>
            <div style={{ height: 150 }}>
              {custDonut ? <Doughnut data={custDonut as any} options={donutOpts as any} /> : <div className="cax-load">—</div>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Mini label="Total customers" value={fmtN(c.total_customers)} />
              <Mini label="Repeat rate" value={fmtPct(c.repeat_purchase_rate)} />
              <Mini label="Avg LTV" value={fmt$(c.avg_lifetime_value)} />
              <Mini label="Purchase freq" value={c.avg_purchase_frequency ? `${c.avg_purchase_frequency.toFixed(2)}x` : '—'} />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue by state — US choropleth */}
      <div style={{ ...panel, marginTop: 16 }}>
        <div style={panelHead}>
          <div style={{ fontWeight: 600 }}>Revenue by state</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Shopify · all-time</div>
        </div>
        <StateRevenueMap data={data.geo_states} height={420} />
      </div>

      <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Revenue, orders, AOV and customers from Shopify. MER = 30-day revenue ÷ Meta spend (the dated channel).
        Channel attribution (last-click, U-shape, Shapley, causal) lives in the Attribution tab.
      </div>
    </div>
  )
}

const panel: React.CSSProperties = {
  padding: 16, borderRadius: 10, background: 'var(--bg-panel)', border: '1px solid var(--border)',
}
const panelHead: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10,
}

function Mini({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  )
}
